<template>
  <div ref="dropdownContainer" class="relative">
    <Fob
      v-if="fob"
      :disabled="disabled"
      icon="<svg width='21' height='21' viewBox='0 0 21 21' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M12 2.61023C12 3.43866 11.3284 4.11023 10.5 4.11023C9.67157 4.11023 9 3.43866 9 2.61023C9 1.7818 9.67157 1.11023 10.5 1.11023C11.3284 1.11023 12 1.7818 12 2.61023ZM12 10.6102C12 11.4387 11.3284 12.1102 10.5 12.1102C9.67157 12.1102 9 11.4387 9 10.6102C9 9.7818 9.67157 9.11023 10.5 9.11023C11.3284 9.11023 12 9.7818 12 10.6102ZM12 18.6102C12 19.4387 11.3284 20.1102 10.5 20.1102C9.67157 20.1102 9 19.4387 9 18.6102C9 17.7818 9.67157 17.1102 10.5 17.1102C11.3284 17.1102 12 17.7818 12 18.6102Z' fill='white' stroke='white'/></svg>"
      :label="label"
      :size="size"
      :type="type"
      @click="toggleDropdown"
    />

    <Button
      v-else
      :disabled="disabled"
      iconAfter="<svg class='w-2.5 h-2.5 ms-3' aria-hidden='true' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 10 6'><path stroke='currentColor' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 1 4 4 4-4'/></svg>"
      :label="label"
      :outline="false"
      :size="size"
      :type="type"
      @click="toggleDropdown"
    />

    <div
      v-if="dropdownVisible"
      class="absolute z-10 bg-white divide-y divide-gray-100 rounded-lg shadow dark:bg-gray-700 dark:divide-gray-600 mt-2.5"
    >
      <div
        v-if="header"
        class="px-4 py-3 text-sm text-gray-900 dark:text-white"
      >
        <div>{{ header.name }}</div>
        <div class="font-medium truncate">{{ header.subheader }}</div>
      </div>

      <slot>
        <ul
          class="text-sm text-gray-700 dark:text-gray-200"
          aria-labelledby="dropdownButton"
        >
          <li
            v-for="(item, index) in items"
            :key="item.label"
            @click="handleSelect(item)"
          >
            <slot name="item" :item="item">
              <div :class="getItemClasses(item, index)">
                <span
                  v-if="item.iconBefore"
                  class="flex items-center me-2"
                  :class="item.alert ? 'text-red-600 dark:text-red-500' : ''"
                  v-html="item.iconBefore"
                ></span>
                <span class="flex-grow">{{ item.label }}</span>
                <span
                  v-if="item.iconAfter"
                  class="flex items-center ms-2"
                  :class="item.alert ? 'text-red-600 dark:text-red-500' : ''"
                  v-html="item.iconAfter"
                ></span>
              </div>
            </slot>
          </li>
        </ul>
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from "vue";

import Button from "../Buttons/Button.vue";
import Fob from "../Fobs/Fob.vue";

const emit = defineEmits(["update:modelValue"]);
interface DropdownItem {
  label: string;
  href: string;
  iconBefore?: string | null;
  iconAfter?: string | null;
  divider?: boolean;
  alert?: boolean;
}

interface DropdownHeader {
  name: string;
  subheader: string;
}

const props = defineProps({
  label: { type: String, default: "Dropdown header" },
  items: { type: Array as () => DropdownItem[], default: () => [] },
  darkMode: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  header: { type: Object as () => DropdownHeader | null, default: null },
  fob: { type: Boolean, default: false },
  size: { type: String, default: "extra-small" },
  type: { type: String, default: "primary" }
});

const dropdownVisible = ref(false);
const dropdownContainer = ref<HTMLElement | null>(null);

const closeDropdown = () => {
  dropdownVisible.value = false;
};

const toggleDropdown = () => {
  if (!props.disabled) {
    dropdownVisible.value = !dropdownVisible.value;
  }
};

const getItemClasses = (item: DropdownItem, index: number) => {
  let classes =
    "flex items-center w-full px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white ";
  if (item.alert) {
    classes +=
      "text-red-600 hover:text-red-600 dark:text-red-500 dark:hover:text-red-500 ";
  }
  if (item.divider) {
    classes += "border-t pt-2 mt-0 ";
  }
  if (index === 0 && !props.header) {
    classes += " rounded-t-lg";
  }
  if (props.header && index === 0) {
    classes += " pt-2";
  }
  if (index > 0 && props.items[index - 1].divider) {
    classes += " mt-0";
  }
  if (index === props.items.length - 1) {
    classes += " rounded-b-lg";
  }
  return classes;
};

const handleSelect = (item: DropdownItem) => {
  emit("update:modelValue", item);
  closeDropdown();
};

// --- Click Outside Logic ---
const handleClickOutside = (event: MouseEvent) => {
  if (
    dropdownContainer.value &&
    !dropdownContainer.value.contains(event.target as Node)
  ) {
    closeDropdown();
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});

defineExpose({ toggleDropdown });
</script>

<style scoped></style>
